import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} OfferVariant
 * @property {number} id
 * @property {string[]} options
 * @property {number} price - Minor units
 * @property {number} compare - Minor units, 0 when unset
 * @property {boolean} available
 * @property {number | null} inventory - null when inventory isn't tracked
 * @property {string} policy - 'deny' or 'continue' (overselling allowed)
 * @property {string | null} image
 */

/**
 * @typedef {object} OfferTotals
 * @property {OfferVariant[]} units
 * @property {number} price - Discounted total
 * @property {number} compare - Undiscounted / compare-at total
 * @property {number} saved
 * @property {number} percent
 */

/**
 * @typedef {object} KocoOffersRefs
 * @property {HTMLScriptElement} variantsData
 * @property {HTMLElement} liveRegion
 * @property {HTMLButtonElement} [addButton]
 * @property {HTMLElement} [buttonLabel]
 * @property {HTMLElement} [buttonTotal]
 * @property {HTMLElement} [error]
 */

/**
 * Bundle/offer picker for the Koco offers block. Each offer card sets a quantity and a
 * discount; the card's price, the main price, the stock message and the button total are
 * recalculated from the variants picked for each unit. Adding to cart posts all units in one
 * request and dispatches the theme's cart events so the drawer and cart icon update.
 *
 * Text templates support: [quantity] [price] [compare_price] [unit_price] [saved_amount]
 * [saved_percent] [count] (stock message only).
 *
 * @extends Component<KocoOffersRefs>
 */
class KocoOffersComponent extends Component {
  requiredRefs = ['variantsData', 'liveRegion'];

  /** @type {OfferVariant[]} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#variants = JSON.parse(this.refs.variantsData.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onCardClick);

    if (!this.#selectedCard && this.#cards[0]) this.#radioOf(this.#cards[0]).checked = true;
    this.#cards.forEach((card) => this.#renderCard(card));
    this.#renderSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onCardClick);
  }

  /** Re-render after the theme editor adds, removes or edits a child block. */
  updatedCallback() {
    super.updatedCallback();
    if (!this.#selectedCard && this.#cards[0]) this.#radioOf(this.#cards[0]).checked = true;
    this.#cards.forEach((card) => this.#renderCard(card));
    this.#renderSelection();
  }

  get #cards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-koco-offer]')));
  }

  get #selectedCard() {
    return this.#cards.find((card) => this.#radioOf(card).checked) ?? null;
  }

  /** @param {HTMLElement} card */
  #radioOf(card) {
    return /** @type {HTMLInputElement} */ (card.querySelector('.koco-offer__radio'));
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.dataset.currency || 'EUR');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = /** @type {HTMLElement | null} */ (target.closest('[data-koco-offer]'));
    if (!card) return;

    if (target.matches('[data-koco-option]')) {
      const row = /** @type {HTMLElement} */ (target.closest('[data-koco-unit]'));
      this.#renderUnit(row);
      this.#renderCard(card);
    }

    this.#renderSelection();
  };

  /** Clicking anywhere on a card (outside its controls) selects it. */
  #onCardClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = /** @type {HTMLElement | null} */ (target.closest('[data-koco-offer]'));
    if (!card || target.closest('select, input, label, a, button')) return;

    const radio = this.#radioOf(card);
    if (radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Resolves the variant chosen in one unit row and updates its thumbnail.
   * @param {HTMLElement} row
   */
  #renderUnit(row) {
    const variant = this.#variantForRow(row);
    row.dataset.variantId = variant ? String(variant.id) : '';
    row.toggleAttribute('data-unavailable', !variant?.available);

    const thumb = /** @type {HTMLImageElement | null} */ (row.querySelector('.koco-offer__thumb'));
    if (thumb && variant?.image) thumb.src = variant.image;
  }

  /** @param {HTMLElement} row */
  #variantForRow(row) {
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(row.querySelectorAll('[data-koco-option]')));
    const chosen = selects.map((select) => select.value);
    return this.#variants.find((variant) => chosen.every((value, index) => variant.options[index] === value));
  }

  /** @returns {OfferVariant | undefined} */
  get #defaultVariant() {
    const id = Number(this.dataset.defaultVariantId);
    return this.#variants.find((variant) => variant.id === id) ?? this.#variants[0];
  }

  /**
   * @param {HTMLElement} card
   * @returns {OfferTotals}
   */
  #totals(card) {
    const quantity = Math.max(1, Number(card.dataset.quantity) || 1);
    const rows = /** @type {HTMLElement[]} */ (Array.from(card.querySelectorAll('[data-koco-unit]')));

    /** @type {OfferVariant[]} */
    let units;
    if (rows.length) {
      units = rows.map((row) => this.#variantForRow(row)).filter((variant) => variant !== undefined);
    } else {
      const variant = this.#defaultVariant;
      units = variant ? Array(quantity).fill(variant) : [];
    }

    const total = units.reduce((sum, variant) => sum + variant.price, 0);
    const compare = units.reduce((sum, variant) => sum + Math.max(variant.compare, variant.price), 0);

    let price = total;
    const value = Number(card.dataset.discountValue) || 0;
    if (card.dataset.discountType === 'percentage') {
      price = Math.round((total * (100 - Math.min(value, 100))) / 100);
    } else if (card.dataset.discountType === 'amount') {
      price = Math.max(0, total - Math.round(value * 100));
    }

    const saved = Math.max(0, compare - price);
    const percent = compare > 0 ? Math.round((saved / compare) * 100) : 0;

    return { units, price, compare, saved, percent };
  }

  /**
   * @param {string} template
   * @param {OfferTotals} totals
   * @param {number} quantity
   */
  #fill(template, totals, quantity) {
    const unit = totals.units[0];
    return template
      .replaceAll('[quantity]', String(quantity))
      .replaceAll('[price]', this.#money(totals.price))
      .replaceAll('[compare_price]', this.#money(totals.compare))
      .replaceAll('[unit_price]', unit ? this.#money(unit.price) : '')
      .replaceAll('[saved_amount]', this.#money(totals.saved))
      .replaceAll('[saved_percent]', String(totals.percent));
  }

  /**
   * Fills every `[data-template]` element in `scope`. Elements whose template mentions savings
   * are hidden when there is nothing saved, so a card never shows "Save €0".
   * @param {ParentNode} scope
   * @param {OfferTotals} totals
   * @param {number} quantity
   */
  #fillTemplates(scope, totals, quantity) {
    for (const element of /** @type {NodeListOf<HTMLElement>} */ (scope.querySelectorAll('[data-template]'))) {
      const template = element.dataset.template || '';
      element.textContent = this.#fill(template, totals, quantity);
      element.hidden = template.includes('[saved_') && totals.saved <= 0;
    }
  }

  /** @param {HTMLElement} card */
  #renderCard(card) {
    card.querySelectorAll('[data-koco-unit]').forEach((row) => this.#renderUnit(/** @type {HTMLElement} */ (row)));

    const totals = this.#totals(card);
    const quantity = Number(card.dataset.quantity) || 1;

    const scope = card.querySelector('.koco-offer__main');
    if (scope) this.#fillTemplates(scope, totals, quantity);

    const priceElement = /** @type {HTMLElement | null} */ (card.querySelector('[data-koco-price]'));
    if (priceElement && !priceElement.hasAttribute('data-override')) priceElement.textContent = this.#money(totals.price);

    const compareElement = /** @type {HTMLElement | null} */ (card.querySelector('[data-koco-compare]'));
    if (compareElement && !compareElement.hasAttribute('data-override')) {
      compareElement.textContent = this.#money(totals.compare);
      compareElement.hidden = totals.compare <= totals.price;
    }
  }

  /** Updates everything that depends on which card is selected. */
  #renderSelection() {
    const card = this.#selectedCard;
    if (!card) return;

    const totals = this.#totals(card);
    const quantity = Number(card.dataset.quantity) || 1;
    const unit = totals.units[0];

    for (const main of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-koco-main-price]'))) {
      const unitTotals = this.#unitTotals(unit);
      const current = /** @type {HTMLElement | null} */ (main.querySelector('[data-koco-main-current]'));
      const compare = /** @type {HTMLElement | null} */ (main.querySelector('[data-koco-main-compare]'));
      if (current) current.textContent = this.#money(unitTotals.price);
      if (compare) {
        compare.textContent = this.#money(unitTotals.compare);
        compare.hidden = unitTotals.saved <= 0;
      }
      this.#fillTemplates(main, unitTotals, 1);
    }

    for (const stock of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-koco-stock]'))) {
      this.#renderStock(stock, unit);
    }

    this.#renderButton(totals, quantity);
  }

  /**
   * Price, compare-at and savings of a single unit (no offer discount).
   * @param {OfferVariant | undefined} unit
   * @returns {OfferTotals}
   */
  #unitTotals(unit) {
    if (!unit) return { units: [], price: 0, compare: 0, saved: 0, percent: 0 };
    const compare = Math.max(unit.compare, unit.price);
    const saved = compare - unit.price;
    return { units: [unit], price: unit.price, compare, saved, percent: compare ? Math.round((saved / compare) * 100) : 0 };
  }

  /**
   * @param {HTMLElement} stock
   * @param {OfferVariant | undefined} unit
   */
  #renderStock(stock, unit) {
    const template = stock.dataset.template || '';
    const text = /** @type {HTMLElement | null} */ (stock.querySelector('[data-koco-stock-text]'));
    const inventory = unit?.inventory ?? null;
    const threshold = Number(stock.dataset.threshold) || 0;

    let visible = Boolean(unit?.available);
    if (template.includes('[count]') && (inventory === null || inventory <= 0)) visible = false;
    if (stock.dataset.visibility === 'low' && (inventory === null || inventory > threshold)) visible = false;

    stock.hidden = !visible;
    if (text) text.textContent = template.replaceAll('[count]', inventory === null ? '' : String(inventory));
  }

  /**
   * @param {OfferTotals} totals
   * @param {number} quantity
   */
  #renderButton(totals, quantity) {
    const { addButton, buttonLabel, buttonTotal } = this.refs;
    if (!addButton) return;

    const soldOut = totals.units.length !== quantity || totals.units.some((variant) => !variant.available);
    const notEnoughStock = !soldOut && this.#exceedsStock(totals.units);
    const available = !soldOut && !notEnoughStock;

    addButton.disabled = !available;
    if (buttonLabel) {
      buttonLabel.textContent = available
        ? addButton.dataset.labelAdd || ''
        : (notEnoughStock ? addButton.dataset.labelUnavailable : addButton.dataset.labelSoldOut) || '';
    }
    if (buttonTotal) {
      buttonTotal.textContent = this.#money(totals.price);
      buttonTotal.hidden = !available;
    }
  }

  /**
   * True when the offer asks for more units of a variant than Shopify will sell.
   * @param {OfferVariant[]} units
   */
  #exceedsStock(units) {
    /** @type {Map<OfferVariant, number>} */
    const counts = new Map();
    for (const variant of units) counts.set(variant, (counts.get(variant) || 0) + 1);
    return Array.from(counts).some(
      ([variant, count]) => variant.policy === 'deny' && variant.inventory !== null && count > variant.inventory
    );
  }

  /** Adds every unit of the selected offer (and its gift, if any) in one request. */
  async addToCart() {
    const card = this.#selectedCard;
    const { addButton, error } = this.refs;
    if (!card || addButton?.disabled) return;

    const totals = this.#totals(card);
    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const variant of totals.units) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);

    const giftId = Number(card.dataset.giftVariantId);
    if (giftId) quantities.set(giftId, (quantities.get(giftId) || 0) + 1);

    const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
    if (!items.length) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    if (error) error.hidden = true;
    addButton?.setAttribute('aria-busy', 'true');
    if (addButton) addButton.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'koco-offers',
          sourceId: this.id,
          itemCount,
          productId: this.dataset.productId,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });

      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showError(response.description || response.message);
        return;
      }

      this.#announce(Theme.translations?.added || '');
    } catch (caught) {
      deferred.reject(caught);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#showError(this.dataset.errorMessage || '');
    } finally {
      addButton?.removeAttribute('aria-busy');
      this.#renderSelection();
    }
  }

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (error && message) {
      error.textContent = message;
      error.hidden = false;
    }
    this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
    setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('koco-offers-component')) {
  customElements.define('koco-offers-component', KocoOffersComponent);
}
